/**
 * SPRINT HISTORY PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useEffect, useState } from 'react';
import { CalendarIcon, BriefcaseIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';
import { WeeklyFocus } from '../types/weeklyFocus';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Screen, Panel, Button, Select } from '../design/primitives';
import { Spinner } from '../components/ui';

const SprintHistoryPage: React.FC = () => {
  const [sprints, setSprints] = useState<WeeklyFocus[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSprints, setExpandedSprints] = useState<Set<number>>(new Set());
  const [groupBy, setGroupBy] = useState<'year' | 'all'>('year');

  useEffect(() => {
    fetchSprints();
  }, []);

  const fetchSprints = async () => {
    try {
      const response = await apiClient.get('/api/weekly-focus/');
      setSprints(response.data);
    } catch (error) {
      console.error('Error fetching sprint history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id: number | undefined) => {
    if (!id) return;
    const newExpanded = new Set(expandedSprints);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSprints(newExpanded);
  };

  const expandAll = () => {
    setExpandedSprints(new Set(sprints.filter(s => s.id).map(s => s.id!)));
  };

  const collapseAll = () => {
    setExpandedSprints(new Set());
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const groupedSprints = () => {
    if (groupBy === 'all') {
      return { 'All Sprints': sprints };
    }

    // Group by year
    const grouped: { [year: string]: WeeklyFocus[] } = {};
    sprints.forEach(sprint => {
      const year = sprint.year.toString();
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(sprint);
    });
    return grouped;
  };

  const getSprintStats = () => {
    const uniqueBusinesses = new Set(sprints.map(s => s.business));
    const yearsActive = new Set(sprints.map(s => s.year));
    return {
      totalSprints: sprints.length,
      uniqueBusinesses: uniqueBusinesses.size,
      yearsActive: yearsActive.size,
    };
  };

  if (loading) {
    return (
      <Screen>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner size="lg" />
            <p style={{
              marginTop: SPACING[16],
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary
            }}>
              Loading sprint history...
            </p>
          </div>
        </div>
      </Screen>
    );
  }

  const stats = getSprintStats();
  const grouped = groupedSprints();

  return (
    <Screen>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: SPACING[32] }}>
          <h1 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.size['5xl'],
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[8]
          }}>
            Sprint History
          </h1>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            margin: 0
          }}>
            View all past weekly business focuses and sprint goals
          </p>
        </div>

        {/* Statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: SPACING[16],
          marginBottom: SPACING[24]
        }}>
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[8]
                }}>
                  Total Sprints
                </p>
                <p style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.size['6xl'],
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.accent.default,
                  margin: 0
                }}>
                  {stats.totalSprints}
                </p>
              </div>
              <CalendarIcon style={{
                width: '48px',
                height: '48px',
                color: COLORS.accent.default,
                opacity: 0.5
              }} />
            </div>
          </Panel>
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[8]
                }}>
                  Businesses Focused
                </p>
                <p style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.size['6xl'],
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.status.info,
                  margin: 0
                }}>
                  {stats.uniqueBusinesses}
                </p>
              </div>
              <BriefcaseIcon style={{
                width: '48px',
                height: '48px',
                color: COLORS.status.info,
                opacity: 0.5
              }} />
            </div>
          </Panel>
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[8]
                }}>
                  Years Active
                </p>
                <p style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.size['6xl'],
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.status.success,
                  margin: 0
                }}>
                  {stats.yearsActive}
                </p>
              </div>
              <CalendarIcon style={{
                width: '48px',
                height: '48px',
                color: COLORS.status.success,
                opacity: 0.5
              }} />
            </div>
          </Panel>
        </div>

        {/* Controls */}
        <Panel style={{ marginBottom: SPACING[24] }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: SPACING[16]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
              <label style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '500',
                color: COLORS.text.primary
              }}>
                Group by:
              </label>
              <Select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'year' | 'all')}
                options={[
                  { value: 'year', label: 'Year' },
                  { value: 'all', label: 'All' }
                ]}
              />
            </div>
            <div style={{ display: 'flex', gap: SPACING[8] }}>
              <Button variant="primary" onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="secondary" onClick={collapseAll}>
                Collapse All
              </Button>
            </div>
          </div>
        </Panel>

        {/* Sprint History */}
        {sprints.length === 0 ? (
          <Panel>
            <div style={{
              textAlign: 'center',
              padding: `${SPACING[48]} ${SPACING[24]}`
            }}>
              <CalendarIcon style={{
                width: '64px',
                height: '64px',
                color: COLORS.text.tertiary,
                margin: '0 auto',
                marginBottom: SPACING[16]
              }} />
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8]
              }}>
                No sprint history yet
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0
              }}>
                Start setting weekly business focuses on your dashboard to build your sprint history.
              </p>
            </div>
          </Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            {Object.entries(grouped).map(([groupName, groupSprints]) => (
              <div key={groupName} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                {/* Year Header (if grouped by year) */}
                {groupBy === 'year' && (
                  <div style={{
                    backgroundColor: COLORS.accent.default,
                    color: '#FFFFFF',
                    borderRadius: RADIUS,
                    padding: SPACING[16],
                    border: `1px solid ${COLORS.border.default}`
                  }}>
                    <h2 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h1,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: SPACING[4]
                    }}>
                      {groupName}
                    </h2>
                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: 0
                    }}>
                      {groupSprints.length} sprint{groupSprints.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                {/* Sprint Cards */}
                {groupSprints.map((sprint) => {
                  const isExpanded = sprint.id ? expandedSprints.has(sprint.id) : false;
                  return (
                    <div
                      key={sprint.id}
                      style={{
                        backgroundColor: COLORS.bg.panel,
                        border: `1px solid ${COLORS.border.default}`,
                        borderLeft: `4px solid ${COLORS.accent.default}`,
                        borderRadius: RADIUS,
                        overflow: 'hidden',
                        transition: 'box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = COLORS.border.hover}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = COLORS.border.default}
                    >
                      {/* Sprint Header */}
                      <div
                        style={{
                          padding: SPACING[24],
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleExpanded(sprint.id)}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: SPACING[16]
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: SPACING[16],
                            flex: 1,
                            minWidth: 0
                          }}>
                            <div style={{
                              backgroundColor: 'rgba(59, 130, 246, 0.15)',
                              borderRadius: RADIUS,
                              padding: SPACING[12],
                              flexShrink: 0
                            }}>
                              <CalendarIcon style={{
                                width: '24px',
                                height: '24px',
                                color: COLORS.accent.default
                              }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: SPACING[8],
                                marginBottom: SPACING[4],
                                flexWrap: 'wrap'
                              }}>
                                <h3 style={{
                                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                                  fontWeight: TYPOGRAPHY.headings.weight,
                                  color: COLORS.text.primary,
                                  margin: 0
                                }}>
                                  Week {sprint.week_number}, {sprint.year}
                                </h3>
                                <span style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.small,
                                  color: COLORS.text.tertiary
                                }}>
                                  {formatDateRange(sprint.start_date, sprint.end_date)}
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: SPACING[8],
                                marginTop: SPACING[8]
                              }}>
                                <BriefcaseIcon style={{
                                  width: '20px',
                                  height: '20px',
                                  color: COLORS.text.tertiary,
                                  flexShrink: 0
                                }} />
                                <p style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  fontWeight: '600',
                                  color: COLORS.text.primary,
                                  margin: 0
                                }}>
                                  {sprint.business_name || 'No business focus'}
                                </p>
                              </div>
                            </div>
                          </div>
                          <button
                            style={{
                              marginLeft: SPACING[16],
                              padding: SPACING[8],
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderRadius: RADIUS,
                              cursor: 'pointer',
                              flexShrink: 0,
                              transition: 'background-color 0.1s ease'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(sprint.id);
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {isExpanded ? (
                              <ChevronUpIcon style={{
                                width: '20px',
                                height: '20px',
                                color: COLORS.text.tertiary
                              }} />
                            ) : (
                              <ChevronDownIcon style={{
                                width: '20px',
                                height: '20px',
                                color: COLORS.text.tertiary
                              }} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{
                          padding: `0 ${SPACING[24]} ${SPACING[24]} ${SPACING[24]}`,
                          borderTop: `1px solid ${COLORS.border.default}`
                        }}>
                          <div style={{
                            paddingTop: SPACING[16],
                            display: 'flex',
                            flexDirection: 'column',
                            gap: SPACING[16]
                          }}>
                            {/* Business Description */}
                            {sprint.business_data?.description && (
                              <div>
                                <p style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.small,
                                  fontWeight: '500',
                                  color: COLORS.text.primary,
                                  margin: 0,
                                  marginBottom: SPACING[8]
                                }}>
                                  Business Description
                                </p>
                                <p style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  color: COLORS.text.secondary,
                                  backgroundColor: COLORS.bg.base,
                                  borderRadius: RADIUS,
                                  padding: SPACING[12],
                                  margin: 0
                                }}>
                                  {sprint.business_data.description}
                                </p>
                              </div>
                            )}

                            {/* Sprint Notes */}
                            <div>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '500',
                                color: COLORS.text.primary,
                                margin: 0,
                                marginBottom: SPACING[8]
                              }}>
                                Sprint Goals & Notes
                              </p>
                              {sprint.notes ? (
                                <div style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  color: COLORS.text.primary,
                                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                  borderRadius: RADIUS,
                                  padding: SPACING[16],
                                  whiteSpace: 'pre-wrap'
                                }}>
                                  {sprint.notes}
                                </div>
                              ) : (
                                <p style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  color: COLORS.text.tertiary,
                                  fontStyle: 'italic',
                                  backgroundColor: COLORS.bg.base,
                                  borderRadius: RADIUS,
                                  padding: SPACING[12],
                                  margin: 0
                                }}>
                                  No sprint notes recorded
                                </p>
                              )}
                            </div>

                            {/* Metadata */}
                            {sprint.created_at && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: SPACING[24],
                                paddingTop: SPACING[8]
                              }}>
                                <span style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                                  color: COLORS.text.tertiary
                                }}>
                                  Created: {new Date(sprint.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                                {sprint.updated_at && sprint.updated_at !== sprint.created_at && (
                                  <span style={{
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.tiny,
                                    color: COLORS.text.tertiary
                                  }}>
                                    Updated: {new Date(sprint.updated_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
};

export default SprintHistoryPage;
